import React, { useEffect, useRef, useCallback, useState } from 'react';
import { isNil, map, get, size, first, times, isEmpty } from 'lodash-es';
import Slider from 'react-slick';
import { useSelector, useDispatch } from 'react-redux';

import { requestNewVideos } from 'pages/home.page';

import { addCarousel } from './actions';
import { viewRangeSelector } from 'common/selectors';
import { getNumberOfVideosPerRow } from 'utils/common';
import { VideoListLoader, Box } from 'components/video-list-loader';
import { carouselChannelSelector } from './selectors';
import { CarouselItem } from './carousel-item';
import { PrevButton, NextButton } from './carousel-buttons';

const useLazyLoad = (channelId) => {
  const dispatch = useDispatch();
  const carouselChannel = useSelector(carouselChannelSelector(channelId));
  const pageNum = get(carouselChannel, 'pageNum', 1);
  const totalNumberOfVideos = get(carouselChannel, 'totalNumberOfVideos', 0);
  const videos = get(carouselChannel, 'videos');

  useEffect(() => {
    dispatch(addCarousel(channelId));
  }, [dispatch, channelId]);

  const videoSize = useRef();
  return (slidesLoaded) => {
    if (totalNumberOfVideos === size(videos)) {
      return;
    }

    if (videos && isNil(videoSize.current)) {
      videoSize.current = size(videos);
    }

    const areWeGettingCloseToTheLastSlides = first(slidesLoaded) + 6 > size(videos);
    if (videoSize.current === size(videos) && areWeGettingCloseToTheLastSlides) {
      const numberOfVideosPerResponse = 12;
      const possibleNumberOfVideos = size(videos) + numberOfVideosPerResponse;
      videoSize.current = Math.min(possibleNumberOfVideos, totalNumberOfVideos);

      dispatch(requestNewVideos(channelId, pageNum, totalNumberOfVideos));
    }
  };
};

export const Carousel = ({ channelId, withRelatedVideos, baseUrl, ariaLabel }) => {
  const carouselChannel = useSelector(carouselChannelSelector(channelId));
  const totalNumberOfVideos = get(carouselChannel, 'totalNumberOfVideos', 0);
  const videos = get(carouselChannel, 'videos');
  const viewRange = useSelector(viewRangeSelector);
  const [currentSlide, setCurrentSlide] = useState(0);

  const numberOfVideosPerRow = getNumberOfVideosPerRow(viewRange, withRelatedVideos);

  const carouselRef = useRef();
  const lazyLoadFn = useLazyLoad(channelId);

  const [buttonHeight, setButtonHeight] = useState(0);
  const thumbnailRef = useCallback((element) => {
    if (element) {
      setButtonHeight(element.getBoundingClientRect().height);
    }
  }, []);

  const sliderSettings = {
    slidesToShow: size(videos) >= numberOfVideosPerRow ? numberOfVideosPerRow : 1,
    slidesPerRow: size(videos) < numberOfVideosPerRow ? numberOfVideosPerRow : 1,
    slidesToScroll: numberOfVideosPerRow,
    prevArrow: <PrevButton height={currentSlide === 0 ? 0 : buttonHeight} />,
    nextArrow: <NextButton height={currentSlide === totalNumberOfVideos - 1 ? 0 : buttonHeight} />,
    infinite: false,
    lazyLoad: 'ondemand',
    afterChange: (index) => {
      const slidesLoaded = [index];
      lazyLoadFn(slidesLoaded);
      setCurrentSlide(index);
    },
  };
  return (
    <div className="ct-videocarousel" data-e2e={`video-carousel-track__${channelId}`} ref={carouselRef}>
      {isEmpty(videos) ? (
        <VideoListLoader
          numRows={1}
          errorMsg={'channel_no_videos'}
          videos={videos}
          isLoading={get(carouselChannel, 'isLoading')}
        />
      ) : (
        <section
          aria-roledescription="carousel"
          aria-label={ariaLabel}
          className="ct-videocarousel__slider"
          data-e2e-current-slide={currentSlide}
        >
          <Slider {...sliderSettings}>
            {map(videos, (video) => (
              <CarouselItem
                key={video.id}
                video={video}
                baseUrl={baseUrl}
                thumbnailRef={thumbnailRef}
                carouselRef={carouselRef}
                withRelatedVideos={withRelatedVideos}
              />
            ))}
            {map(times(totalNumberOfVideos - size(videos)), (idx) => (
              <Box key={idx} boxNumber={numberOfVideosPerRow}></Box>
            ))}
          </Slider>
        </section>
      )}
    </div>
  );
};
